"use client";

/**
 * Storefront card for a menu item: photo, name, a line of description,
 * dietary badges and the price (or the "from" price when portions differ).
 * Made-to-order items are always orderable; stock-tracked ones (bottled
 * drinks) show sold out at zero.
 */

import { useState } from "react";
import { discountPct } from "@/lib/config/sale";
import Link from "next/link";
import { Flame, Leaf, Plus } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { toast } from "@/lib/toast";
import { useCartStore } from "@/lib/store/cartStore";
import { ShopProduct } from "@/lib/types/shop";
import {
  FOOD_PLACEHOLDER_IMAGE,
  foodPortions,
  foodPriceRange,
  isFoodAvailable,
  isStockTracked,
} from "@/lib/config/food";
import { FoodPortionSelector } from "./FoodPortionSelector";
import { WebsitePerkTags } from "@/components/product/WebsitePerks";

interface FoodShopProductCardProps {
  product: ShopProduct & Record<string, any>;
  className?: string;
  productUrlPrefix?: string;
}

const money = (n: number) => `UGX ${Math.round(n).toLocaleString()}`;

export function FoodShopProductCard({ product, className, productUrlPrefix = "" }: FoodShopProductCardProps) {
  const [adding, setAdding] = useState(false);
  const [showPortions, setShowPortions] = useState(false);
  const { initializeSession, openCart, sessionId } = useCartStore();
  const addToCartMutation = useMutation(api.cart.core.addToCart);

  const brandId = (product.brandId || "") as Id<"brands">;
  const portions = foodPortions(product);
  const range = foodPriceRange(product);
  const tracked = isStockTracked(product);
  const available = isFoodAvailable(product);
  const totalStock = Number(product.totalStock ?? product.stock ?? 0);
  const soldOut = tracked && totalStock <= 0;
  const canOrder = available && !soldOut;

  const image = product.images?.[0] || product.imageUrl || FOOD_PLACEHOLDER_IMAGE;
  const tags: string[] = product.food?.dietaryTags || product.tags || [];
  const spice = Number(product.food?.spiceLevel ?? 0);
  const productUrl = `${productUrlPrefix}/product/${product.slug || product._id}`;
  const priceLabel = range.min === range.max ? money(range.min) : `from ${money(range.min)}`;
  const priceHidden = product.priceHidden || product.isCustomPrice;

  const addWith = async (quantity: number, variantId?: string, selectedVariants?: Record<string, any>) => {
    initializeSession();
    await addToCartMutation({
      productId: product._id as Id<"products">,
      brandId,
      quantity,
      variantId,
      selectedVariants: selectedVariants as any,
      sessionId: sessionId || undefined,
    });
    toast.success(`${product.name} added to your order`);
    openCart();
  };

  const handleAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canOrder || adding) return;
    if (portions.length > 0) {
      setShowPortions(true);
      return;
    }
    setAdding(true);
    try {
      await addWith(1);
    } catch (err: any) {
      toast.error(err?.message || "Failed to add to cart");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className={`group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xs transition-all duration-300 hover:shadow-lg ${className || ""}`}>
      <Link href={productUrl} className="relative block aspect-square w-full overflow-hidden bg-gray-50">
        <img
          src={image}
          alt={product.name}
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
        />
        {!canOrder && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/55">
            <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-gray-900">
              {!available ? "Off menu today" : "Sold out"}
            </span>
          </div>
        )}
        {canOrder && (
          <button
            onClick={handleAdd}
            disabled={adding}
            className={`absolute bottom-2 right-2 flex items-center gap-1 rounded-full border px-2.5 py-1.5 shadow-md transition-all duration-300 ${
              adding ? "border-green-500 bg-green-500 text-white" : "border-gray-100 bg-white text-black hover:border-black hover:bg-black hover:text-white"
            }`}
            aria-label={portions.length > 0 ? "Choose portion" : "Add to order"}
          >
            <Plus className="h-4 w-4" strokeWidth={2.5} />
            <span className="text-[10px] font-semibold">{portions.length > 0 ? "Choose" : "Add"}</span>
          </button>
        )}
      </Link>

      <div className="flex flex-1 flex-col px-3 py-2.5">
        <Link href={productUrl} className="block min-w-0">
          <h3 className="truncate text-sm font-semibold leading-tight text-gray-900 transition-colors group-hover:text-orange-700">
            {product.name}
          </h3>
        </Link>
        {product.description && (
          <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-gray-500">{product.description}</p>
        )}
        {(tags.length > 0 || spice > 0) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {tags.slice(0, 3).map((t) => (
              <span key={t} className="inline-flex items-center gap-0.5 rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[9px] font-medium text-gray-600">
                {(t === "Vegetarian" || t === "Vegan") && <Leaf className="h-2.5 w-2.5 text-green-600" />}
                {t}
              </span>
            ))}
            {spice > 0 && (
              <span className="inline-flex items-center text-orange-500" title="Spice level">
                {Array.from({ length: spice }).map((_, i) => (
                  <Flame key={i} className="h-3 w-3" />
                ))}
              </span>
            )}
          </div>
        )}
        {/* Free delivery, and its add-ons (website, Oct 2026). */}
        <WebsitePerkTags product={product} className="mt-1.5" />
        <div className="mt-auto flex items-baseline justify-between pt-1.5">
          <span className="text-sm font-bold text-gray-900">
            {priceHidden ? "Ask for price" : priceLabel}
            {!priceHidden && discountPct(product.price, product.comparePrice) ? (
              <span className="ml-1.5 text-[10px] font-normal text-gray-400 line-through">{money(Number(product.comparePrice) || 0)}</span>
            ) : null}
          </span>
          {portions.length > 0 && <span className="text-[10px] text-gray-400">{portions.length} sizes</span>}
        </div>
      </div>

      <FoodPortionSelector
        isOpen={showPortions}
        onClose={() => setShowPortions(false)}
        product={product}
        onConfirm={addWith}
      />
    </div>
  );
}
